// src/utils/advancedPreloading.js
// Advanced Preloading Strategies for ADTAC Website

/**
 * Advanced Preloading Manager
 * Implements intelligent preloading based on user behavior and page importance
 */
class AdvancedPreloadingManager {
  constructor() {
    this.preloadedRoutes = new Set();
    this.preloadQueue = [];
    this.isPreloading = false;
    this.userBehavior = {
      mouseMoveCount: 0,
      scrollDepth: 0,
      timeOnPage: 0,
      lastActivity: Date.now()
    };
    
    this.init();
  }

  init() {
    this.trackUserBehavior();
    this.implementIntelligentPreloading();
    this.addResourceHints();
  }

  /**
   * Add Resource Hints for Critical Dependencies
   */
  addResourceHints() {
    const hints = [
      // DNS Prefetch for external domains
      { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
      { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
      { rel: 'dns-prefetch', href: 'https://www.googletagmanager.com' },
      { rel: 'dns-prefetch', href: 'https://www.google-analytics.com' },
      
      // Preconnect to critical domains with CORS
      { rel: 'preconnect', href: 'https://fonts.googleapis.com', crossorigin: 'anonymous' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' },
      
      // Prefetch critical routes
      { rel: 'prefetch', href: '/consultancy-services', as: 'document' },
      { rel: 'prefetch', href: '/contact', as: 'document' },
      { rel: 'prefetch', href: '/consultancy-services/iso-22000', as: 'document' },
      { rel: 'prefetch', href: '/consultancy-services/haccp', as: 'document' },
      { rel: 'prefetch', href: '/consultancy-services/fssc', as: 'document' },
      
      // Prefetch critical images
      { rel: 'prefetch', href: '/assets/images/logo/adtac-logo.png', as: 'image' },
      { rel: 'prefetch', href: '/assets/images/services/carousel-4-haccp.webp', as: 'image' },
      { rel: 'prefetch', href: '/assets/images/services/carousel-7-gdpmd.webp', as: 'image' },
      
      // Prefetch critical stylesheets
      { rel: 'prefetch', href: '/assets/css/critical.css', as: 'style' },
      
      // Preload critical fonts
      { rel: 'preload', href: 'https://fonts.gstatic.com/s/opensans/v34/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0B4gaVc.woff2', as: 'font', type: 'font/woff2', crossorigin: 'anonymous' },
      { rel: 'preload', href: 'https://fonts.gstatic.com/s/exo/v20/4UaOrEtFpBI4f1ZSIK9d4LjJ4lM2CwNsOl4p5Is.woff2', as: 'font', type: 'font/woff2', crossorigin: 'anonymous' }
    ];

    hints.forEach(hint => {
      if (!document.querySelector(`link[href="${hint.href}"]`)) {
        const link = document.createElement('link');
        link.rel = hint.rel;
        link.href = hint.href;
        
        if (hint.as) link.as = hint.as;
        if (hint.type) link.type = hint.type;
        if (hint.crossorigin) link.crossOrigin = hint.crossorigin;
        
        document.head.appendChild(link);
      }
    });

    console.log('✅ Advanced resource hints added');
  }

  /**
   * Track User Behavior for Intelligent Preloading
   */
  trackUserBehavior() {
    let startTime = Date.now();

    // Track mouse movement (engagement indicator)
    document.addEventListener('mousemove', () => {
      this.userBehavior.mouseMoveCount++;
      this.userBehavior.lastActivity = Date.now();
      
      // If user is highly engaged, preload more aggressively
      if (this.userBehavior.mouseMoveCount > 50 && !this.preloadedRoutes.has('engagement-triggered')) {
        this.preloadedRoutes.add('engagement-triggered');
        this.preloadSecondaryRoutes();
      }
    }, { passive: true });

    // Track scroll depth
    window.addEventListener('scroll', () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      this.userBehavior.scrollDepth = Math.max(this.userBehavior.scrollDepth, scrollPercent);
      this.userBehavior.lastActivity = Date.now();
      
      // If user scrolls beyond 25%, they're engaged - preload more
      if (scrollPercent > 25 && !this.preloadedRoutes.has('scroll-triggered')) {
        this.preloadedRoutes.add('scroll-triggered');
        this.preloadSecondaryRoutes();
      }
    }, { passive: true });

    // Track time on page
    setInterval(() => {
      this.userBehavior.timeOnPage = Date.now() - startTime;
      
      // If user stays more than 10 seconds, preload related content
      if (this.userBehavior.timeOnPage > 10000 && !this.preloadedRoutes.has('time-triggered')) {
        this.preloadedRoutes.add('time-triggered');
        this.preloadRelatedContent();
      }
    }, 5000);
  }

  /**
   * Implement Intelligent Preloading based on Current Page
   */
  implementIntelligentPreloading() {
    const currentPath = window.location.pathname;
    
    // Preload based on current page context
    setTimeout(() => {
      this.preloadBasedOnContext(currentPath);
    }, 2000);

    // Hover-based preloading for navigation links
    this.implementHoverPreloading();
  }

  /**
   * Preload Based on Current Page Context
   */
  preloadBasedOnContext(currentPath) {
    const preloadStrategies = {
      '/': [
        '/consultancy-services',
        '/about-us',
        '/contact'
      ],
      '/consultancy-services': [
        '/consultancy-services/iso-22000',
        '/consultancy-services/haccp',
        '/consultancy-services/fssc',
        '/contact'
      ],
      '/about-us': [
        '/consultancy-services',
        '/management-system-training',
        '/contact'
      ],
      '/contact': [
        '/consultancy-services',
        '/',
        '/about-us'
      ]
    };

    const routesToPreload = preloadStrategies[currentPath] || [];
    
    routesToPreload.forEach((route, index) => {
      setTimeout(() => {
        this.preloadRoute(route);
      }, index * 1000); // Stagger preloading
    });
  }

  /**
   * Implement Hover-based Preloading
   */
  implementHoverPreloading() {
    // Preload on navigation hover
    document.addEventListener('mouseover', (event) => {
      const link = event.target.closest('a[href]');
      if (link && link.href && link.href.includes(window.location.origin)) {
        const path = new URL(link.href).pathname;
        this.preloadRoute(path);
      }
    });

    // Preload service cards on hover
    const serviceSelectors = [
      '.service-card',
      '.certification-card', 
      '.training-card',
      '[href*="/consultancy-services/"]'
    ];

    serviceSelectors.forEach(selector => {
      document.addEventListener('mouseover', (event) => {
        if (event.target.matches(selector) || event.target.closest(selector)) {
          const link = event.target.querySelector('a[href]') || event.target.closest('a[href]');
          if (link) {
            const path = new URL(link.href).pathname;
            this.preloadRoute(path);
          }
        }
      });
    });
  }

  /**
   * Preload Route with Prefetch Link
   */
  preloadRoute(route) {
    if (this.preloadedRoutes.has(route)) return;
    
    this.preloadedRoutes.add(route);
    
    // Add prefetch link
    const prefetchLink = document.createElement('link');
    prefetchLink.rel = 'prefetch';
    prefetchLink.href = route;
    prefetchLink.as = 'document';
    document.head.appendChild(prefetchLink);
    
    console.log(`🚀 Prefetching route: ${route}`);
  }

  /**
   * Preload Secondary Routes (less critical)
   */
  preloadSecondaryRoutes() {
    const secondaryRoutes = [
      '/gallery',
      '/blog',
      '/consultancy-services/gmp',
      '/consultancy-services/iso-9001',
      '/consultancy-services/gdpmd',
      '/management-system-training'
    ];

    secondaryRoutes.forEach((route, index) => {
      setTimeout(() => {
        this.preloadRoute(route);
      }, index * 2000); // Slower staggering for secondary content
    });
  }

  /**
   * Preload Related Content based on current page
   */
  preloadRelatedContent() {
    const currentPath = window.location.pathname;
    
    if (currentPath.includes('/consultancy-services/')) {
      // If on a service page, preload other services
      const otherServices = [
        '/consultancy-services/iso-22000',
        '/consultancy-services/haccp', 
        '/consultancy-services/fssc',
        '/consultancy-services/gmp',
        '/consultancy-services/iso-9001'
      ].filter(service => service !== currentPath);

      otherServices.forEach((service, index) => {
        setTimeout(() => {
          this.preloadRoute(service);
        }, index * 3000);
      });
    }

    if (currentPath === '/blog') {
      // Preload blog posts
      const blogPosts = [
        '/blog/apa-itu-gmp',
        '/blog/toilet-gmp-apa-kaitannya',
        '/blog/nyahbeku-dengan-rendam-makanan-dalam-air'
      ];

      blogPosts.forEach((post, index) => {
        setTimeout(() => {
          this.preloadRoute(post);
        }, index * 2000);
      });
    }
  }

  /**
   * Preload Critical Images
   */
  preloadCriticalImages() {
    const criticalImages = [
      '/assets/images/logo/adtac-logo.png',
      '/assets/images/services/carousel-4-haccp.webp',
      '/assets/images/services/carousel-7-gdpmd.webp',
      '/assets/images/home/hero-banner.webp'
    ];

    criticalImages.forEach(imageSrc => {
      if (!document.querySelector(`link[href="${imageSrc}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = imageSrc;
        link.as = 'image';
        document.head.appendChild(link);
      }
    });
  }

  /**
   * Network-aware preloading
   */
  implementNetworkAwarePreloading() {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      
      // Adjust preloading based on connection
      if (connection.effectiveType === '4g' && !connection.saveData) {
        // Aggressive preloading for fast connections
        this.preloadSecondaryRoutes();
        this.preloadCriticalImages();
      } else if (connection.effectiveType === 'slow-2g' || connection.saveData) {
        // Minimal preloading for slow connections
        console.log('Slow connection detected, reducing preloading');
        return;
      }
    }
  }
}

// Initialize Advanced Preloading
export const initAdvancedPreloading = () => {
  if (typeof window === 'undefined') return;
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new AdvancedPreloadingManager();
    });
  } else {
    new AdvancedPreloadingManager();
  }
};

export default AdvancedPreloadingManager;